import React, { useEffect, useState } from "react";
import { Wallet, ExternalLink, ImageOff } from "lucide-react";
import client from "../lib/api";

const NFTs = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletAddr, setWalletAddr] = useState(localStorage.getItem("wallet_addr") || "");
  const [walletNfts, setWalletNfts] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [manualAddr, setManualAddr] = useState("");

  useEffect(() => {
    client.get("/opensea/collections?chain=ethereum&limit=24").then(({ data }) => {
      setCollections(data.collections || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadNfts = async (address) => {
    if (!address) return;
    setWalletLoading(true);
    try {
      const { data } = await client.get(`/opensea/wallet/${address}`);
      setWalletNfts(data.nfts || []);
    } catch (e) {
      console.error(e);
      setWalletNfts([]);
    } finally {
      setWalletLoading(false);
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) { alert("Install MetaMask first."); return; }
    try {
      const accs = await window.ethereum.request({ method: "eth_requestAccounts" });
      if (accs[0]) {
        setWalletAddr(accs[0]);
        localStorage.setItem("wallet_addr", accs[0]);
        loadNfts(accs[0]);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (walletAddr) loadNfts(walletAddr);
  }, [walletAddr]);

  return (
    <div className="space-y-12 fade-in-up" data-testid="nfts-page">
      {/* Hero */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <span className="chip">OpenSea · Ethereum</span>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">NFT Market & Your Wallet</h1>
          <p className="text-neutral-400 mt-2 text-sm">Top collections by market cap, plus a view of NFTs in any wallet.</p>
        </div>
        <button data-testid="nft-connect-wallet" onClick={connectWallet} className="btn-accent inline-flex items-center gap-2">
          <Wallet size={16} />
          {walletAddr ? `${walletAddr.slice(0, 6)}…${walletAddr.slice(-4)}` : "Connect Wallet"}
        </button>
      </div>

      {/* Wallet NFTs */}
      <section>
        <div className="glass p-6 mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="text-xs uppercase tracking-widest text-neutral-500">View any wallet</div>
            <input
              data-testid="nft-wallet-input"
              value={manualAddr}
              onChange={(e) => setManualAddr(e.target.value)}
              placeholder="0x… address"
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-white/20 font-mono-num"
            />
            <button data-testid="nft-wallet-load" onClick={() => { setWalletAddr(manualAddr); }} className="btn-glass !py-2 !px-4 text-sm">Load NFTs</button>
          </div>
          {walletAddr && (
            <div className="mt-3 text-xs text-neutral-400 font-mono-num">Showing NFTs for: {walletAddr}</div>
          )}
        </div>

        {walletAddr && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {walletLoading && Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="glass aspect-square animate-pulse" />
            ))}
            {!walletLoading && walletNfts.length === 0 && (
              <div className="col-span-full text-neutral-500 text-sm text-center py-8">No NFTs found for this wallet.</div>
            )}
            {!walletLoading && walletNfts.map((n, i) => (
              <div key={`${n.contract}-${n.identifier}-${i}`} data-testid={`wallet-nft-${i}`} className="glass glass-hover overflow-hidden">
                <div className="aspect-square bg-black/40 flex items-center justify-center">
                  {n.image_url ? (
                    <img src={n.image_url} alt={n.name} className="w-full h-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                  ) : (
                    <ImageOff size={24} className="text-neutral-600" />
                  )}
                </div>
                <div className="p-3">
                  <div className="text-xs truncate font-medium">{n.name || `#${n.identifier}`}</div>
                  <div className="text-[10px] text-neutral-500 truncate">{n.collection}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Top Collections */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-neutral-500">Top Collections</div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight mt-1">Trending on OpenSea</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && Array.from({ length: 6 }).map((_, i) => <div key={i} className="glass h-64 animate-pulse" />)}
          {!loading && collections.map((c, i) => (
            <a
              key={c.collection || i}
              href={c.opensea_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`collection-${c.collection}`}
              className="glass glass-hover overflow-hidden block"
            >
              <div className="relative h-32 bg-white/[0.03]">
                {c.banner_image_url && <img src={c.banner_image_url} alt="" className="w-full h-full object-cover" onError={(e) => (e.target.style.display = "none")} />}
              </div>
              <div className="p-5 -mt-8 relative">
                <div className="flex items-end gap-3">
                  <div className="w-14 h-14 rounded-xl border-2 border-black/80 bg-black overflow-hidden flex-shrink-0">
                    {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg truncate flex items-center gap-1">
                      {c.name}
                      {c.safelist_status === "verified" && <span className="text-blue-400 text-sm">✓</span>}
                    </div>
                  </div>
                  <ExternalLink size={14} className="text-neutral-500" />
                </div>
                <p className="text-xs text-neutral-400 mt-3 line-clamp-2">{c.description || "—"}</p>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-3">Supply: <span className="font-mono-num text-neutral-300">{c.total_supply ?? "—"}</span></div>
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
};

export default NFTs;
